import { keyBy } from 'lodash-es';

import type { JobsJobElasticSchemaWithId } from '@iconik-io/iconik-api/src/services/jobs';

import type { JobType, WidgetType } from '../ducks/jobs_widgets/types';
import type { IconType } from '../icons/constants';
import { ICONS } from '../icons/icon';
import type { VariantType } from './variants';

export const jobTypes: {
  label: string;
  value: JobType;
  icon: IconType;
}[] = [
  { label: 'Transcode', value: 'TRANSCODE', icon: ICONS.Transcoder },
  { label: 'Keyframes', value: 'KEYFRAMES', icon: ICONS.Camera },
  { label: 'Export', value: 'EXPORT', icon: ICONS.Export },
  { label: 'Delete', value: 'DELETE', icon: ICONS.TrashBin },
  { label: 'Index', value: 'REINDEX', icon: ICONS.Index },
  { label: 'Scan', value: 'SCAN', icon: ICONS.Index },
  { label: 'Move', value: 'MOVE', icon: ICONS.Move },
  { label: 'Custom', value: 'CUSTOM', icon: ICONS.Custom },
  { label: 'Metadata update', value: 'METADATA', icon: ICONS.Metadata },
  { label: 'Analyze', value: 'ANALYZE', icon: ICONS.Analyze },
  { label: 'Transfer/Copy', value: 'TRANSFER', icon: ICONS.Transfer },
  { label: 'Archive', value: 'ARCHIVE', icon: ICONS.Archive },
  { label: 'Restore', value: 'RESTORE', icon: ICONS.Restore },
  {
    label: 'Glacier Restore',
    value: 'RESTORE_FROM_GLACIER',
    icon: ICONS.Restore,
  },
  { label: 'Transcribe', value: 'TRANSCRIPTION', icon: ICONS.Transcription },
  {
    label: 'Collection Map Request',
    value: 'REQUEST_COLLECTION_MAP',
    icon: ICONS.CollectionIsg,
  },
  {
    label: 'Face recognition',
    value: 'FACE_RECOGNITION',
    icon: ICONS.FaceRecognition,
  },
  {
    label: 'Change person',
    value: 'CHANGE_PERSON',
    icon: ICONS.FaceRecognition,
  },
  {
    label: 'Delete person',
    value: 'DELETE_PERSON',
    icon: ICONS.FaceRecognition,
  },
  { label: 'Automation', value: 'AUTOMATION', icon: ICONS.Automations },
  {
    label: 'Review request',
    value: 'REVIEW_REQUEST',
    icon: ICONS.ThumbMixed,
  },
  {
    label: 'Publish',
    value: 'PUBLISH',
    icon: ICONS.Publish,
  },
  {
    label: 'AI Metadata Enrichment',
    value: 'METADATA_FILLING',
    icon: ICONS.Ai,
  },
  {
    label: 'Publish Scheduled',
    value: 'PUBLISH_SCHEDULED',
    icon: ICONS.Publish,
  },
];

export const jobTypeLabelsPlural: { [type in JobType]: string } = {
  TRANSCODE: 'Transcodes',
  KEYFRAMES: 'Keyframes',
  EXPORT: 'Exports',
  DELETE: 'Deletions',
  REINDEX: 'Indices',
  SCAN: 'Scans',
  MOVE: 'Moves',
  CUSTOM: 'Custom',
  METADATA: 'Metadata Updates',
  ANALYZE: 'Analyzations',
  TRANSFER: 'Transfers',
  ARCHIVE: 'Archiving',
  RESTORE: 'Restores',
  RESTORE_FROM_GLACIER: 'Glacier Restores',
  TRANSCRIPTION: 'Transcriptions',
  ACL: 'ACLs',
  COPY: 'Copies',
  MEDIAINFO: 'MediaInfo',
  REQUEST_COLLECTION_MAP: 'Collection Map Requests',
  COLLECTION_CUSTOM_ORDER: 'Collection Custom Orders',
  STORAGE_GATEWAY_FILE_INGEST: 'Storage Gateway File Ingest',
  MARK_MISSING: 'Mark missing',
  FACE_RECOGNITION: 'Face recognition',
  SET_APPROVAL: 'Set approval',
  CHANGE_PERSON: 'Change persons',
  DELETE_PERSON: 'Delete persons',
  AUTOMATION: 'Automations',
  CONFIRM_PERSON: 'Confirm person',
  REVIEW_REQUEST: 'Review requests',
  CONVERT_SAVED_SEARCH_TO_COLLECTION: 'Create collection from saved search',
  CONVERT_SEARCH_TO_COLLECTION: 'Create collection from search',
  REQUEST_APPROVAL: 'Request approval',
  DELETE_JOBS: 'Delete jobs',
  EDIT_JOBS: 'Edit jobs',
  PUBLISH: 'Publish',
  METADATA_FILLING: 'Metadata Enrichments',
  PUBLISH_SCHEDULED: 'Publish Scheduled',
};

export const jobObjectType = [
  { label: 'Assets', value: 'assets', icon: ICONS.File },
  { label: 'Collections', value: 'collections', icon: ICONS.Collection },
  { label: 'Users', value: 'users', icon: ICONS.User },
  { label: 'Groups', value: 'groups', icon: ICONS.Group },
  { label: 'Files', value: 'files', icon: ICONS.File },
  { label: 'Segments', value: 'segments', icon: ICONS.Comment },
  { label: 'Storages', value: 'storages', icon: ICONS.Storage },
  { label: 'Transcoders', value: 'transcoders', icon: ICONS.Transcoder },
];

export const JOB_STATUS_FINISHED = 'FINISHED';
export const JOB_STATUS_FAILED = 'FAILED';
export const JOB_STATUS_READY = 'READY';
export const JOB_STATUS_STARTED = 'STARTED';
export const JOB_STATUS_WAITING = 'WAITING';
export const JOB_STATUS_ABORT_PENDING = 'ABORT_PENDING';
export const JOB_STATUS_ABORTED = 'ABORTED';
export const JOB_STATUS_SKIPPED = 'SKIPPED';
export const JOB_FINISHED_WITH_WARNING = 'FINISHED_WITH_WARNING';
export const JOB_STATUS_PAUSED = 'PAUSED';
export const JOB_STATUS_PENDING_USER = 'PENDING_USER';

export const JOB_COMPLETED_STATUSES_SET = new Set([
  JOB_STATUS_FINISHED,
  JOB_STATUS_SKIPPED,
  JOB_STATUS_ABORTED,
  JOB_STATUS_FAILED,
  JOB_FINISHED_WITH_WARNING,
]);

export const JOB_IS_NOT_ABORTABLE_SET = new Set<JobType>([
  'PUBLISH_SCHEDULED',
  'PUBLISH',
]);

export type JobStatus = JobsJobElasticSchemaWithId['status'];

type JobStatusObject = {
  label: string;
  value: JobStatus;
  bsStyle: VariantType;
};
export const jobStatus: JobStatusObject[] = [
  { label: 'Finished', value: JOB_STATUS_FINISHED, bsStyle: 'success' },
  {
    label: 'Finished with warning',
    value: JOB_FINISHED_WITH_WARNING,
    bsStyle: 'success',
  },
  { label: 'Failed', value: JOB_STATUS_FAILED, bsStyle: 'danger' },
  { label: 'Ready', value: JOB_STATUS_READY, bsStyle: 'info' },
  { label: 'Started', value: JOB_STATUS_STARTED, bsStyle: 'info' },
  { label: 'Waiting', value: JOB_STATUS_WAITING, bsStyle: 'warning' },
  {
    label: 'Abort Pending',
    value: JOB_STATUS_ABORT_PENDING,
    bsStyle: 'warning',
  },
  { label: 'Aborted', value: JOB_STATUS_ABORTED, bsStyle: 'danger' },
  { label: 'Skipped', value: JOB_STATUS_SKIPPED, bsStyle: 'info' },
  { label: 'Paused', value: JOB_STATUS_PAUSED, bsStyle: 'info' },
  {
    label: 'Pending review',
    value: JOB_STATUS_PENDING_USER,
    bsStyle: 'warning',
  },
];

export const jobsStatusMap: { [key in JobStatus]?: JobStatusObject } = keyBy(
  jobStatus,
  (status) => status.value,
);

export const isJobStatus = (value: string): value is JobStatus =>
  Object.hasOwn(jobsStatusMap, value);

type JobStepStatusObject = {
  label: string;
  value: string;
  bsStyle: VariantType;
};
export const jobStepStatus: JobStepStatusObject[] = [
  { label: 'Waiting', value: 'WAITING', bsStyle: 'info' },
  { label: 'In Progress', value: 'IN_PROGRESS', bsStyle: 'warning' },
  { label: 'Failed', value: 'FAILED', bsStyle: 'danger' },
  { label: 'Done', value: 'DONE', bsStyle: 'success' },
  { label: 'Skipped', value: 'SKIPPED', bsStyle: 'info' },
];

export const jobsStepStatusMap: Record<string, JobStepStatusObject> = keyBy(
  jobStepStatus,
  (status) => status.value,
);

export const objectTypeOptions = [
  { label: 'Assets', value: 'assets' },
  { label: 'Collections', value: 'collections' },
  { label: 'Storages', value: 'storages' },
  { label: 'Saved searches', value: 'saved_searches' },
  { label: 'Export locations ', value: 'export_locations' },
];

export type JobColumnType =
  | 'title'
  | 'created_by'
  | 'status'
  | 'type'
  | 'date_created'
  | 'date_modified'
  | 'object_type'
  | 'progress'
  | 'progress_processed'
  | 'priority'
  | 'storage';

type ColumnOption = {
  label: string;
  value: JobColumnType;
};

const TITLE_COLUMN: ColumnOption = { label: 'Title', value: 'title' };
const USER_COLUMN: ColumnOption = { label: 'User', value: 'created_by' };
const STATUS_COLUMN: ColumnOption = { label: 'Status', value: 'status' };
const TYPE_COLUMN: ColumnOption = { label: 'Type', value: 'type' };
const DATE_CREATED_COLUMN: ColumnOption = {
  label: 'Date created',
  value: 'date_created',
};
const DATE_MODIFIED_COLUMN: ColumnOption = {
  label: 'Last updated',
  value: 'date_modified',
};
const OBJECT_TYPE_COLUMN: ColumnOption = {
  label: 'Object type',
  value: 'object_type',
};
const PROGRESS_COLUMN: ColumnOption = {
  label: 'Progress',
  value: 'progress_processed',
};
const PRIORITY_COLUMN: ColumnOption = { label: 'Priority', value: 'priority' };

const STORAGE_COLUMN: ColumnOption = { label: 'Storage', value: 'storage' };

export const columnsOptions: ColumnOption[] = [
  TITLE_COLUMN,
  USER_COLUMN,
  STATUS_COLUMN,
  TYPE_COLUMN,
  DATE_CREATED_COLUMN,
  DATE_MODIFIED_COLUMN,
  OBJECT_TYPE_COLUMN,
  PROGRESS_COLUMN,
  PRIORITY_COLUMN,
  STORAGE_COLUMN,
];

export const sortValues: ColumnOption[] = [
  TITLE_COLUMN,
  USER_COLUMN,
  STATUS_COLUMN,
  TYPE_COLUMN,
  DATE_CREATED_COLUMN,
  DATE_MODIFIED_COLUMN,
  OBJECT_TYPE_COLUMN,
  PRIORITY_COLUMN,
];

export const maxResultsOptions = [
  { label: '25', value: '25' },
  { label: '50', value: '50' },
  { label: '100', value: '100' },
];

export const sortOptions = [
  { label: 'Ascending', value: 'asc' },
  { label: 'Descending', value: 'desc' },
];

type WidgetTypeObject = {
  value: WidgetType;
  title: string;
  description: string;
  icon: IconType;
};

export const widgetTypes: WidgetTypeObject[] = [
  {
    value: 'JOBS_LIST',
    title: 'Jobs list',
    description:
      'Create a widget to monitor a list of iconik jobs and use filters to focus on certain job types',
    icon: 'List',
  },
  {
    value: 'JOBS_STATS_LIST',
    title: 'Jobs stats list',
    description: 'Create a widget to monitor iconik jobs with job statistics',
    icon: 'Stat',
  },
];
